import { useEffect, useState } from "react";
import { Check, Settings } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { TosAgreement } from "@/components/TalentPass";
import api, { fmtErr, openExternal } from "@/lib/api";

export default function PlansSection() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.filter((p) => p.audience === "employer"))).catch(() => {});
  }, []);

  const plan = user?.plan;

  const subscribe = async (lookupKey) => {
    if (!agreed) return toast.error("Please agree to the Terms of Service and Refund Policy first.");
    setBusy(lookupKey);
    try {
      const { data } = await api.post("/payments/subscribe", { lookup_key: lookupKey, origin_url: window.location.origin, agreed });
      openExternal(data.checkout_url);
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      openExternal(data.portal_url);
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(null);
    }
  };

  return (
    <section className="mb-16" data-testid="plans-section">
      <div className="flex items-end justify-between mb-8">
        <p className="overline-label !font-semibold !text-white/85">Posting bundles</p>
        {plan && (
          <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-accent" data-testid="plan-usage">
            {plan.name} — {plan.posts_limit === null ? "Unlimited posts" : `${plan.posts_used}/${plan.posts_limit} posts used this month`}
          </p>
        )}
      </div>
      <TosAgreement agreed={agreed} setAgreed={setAgreed} testId="plans-tos" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-8">
        {plans.map((p) => {
          const current = plan?.key === p.lookup_key;
          const trialOffer = !plan && !user?.trial_used && p.trial_days;
          return (
            <div key={p.lookup_key} className={`bg-background p-8 flex flex-col ${current ? "outline outline-1 outline-accent -outline-offset-1" : ""}`} data-testid={`plan-card-${p.lookup_key}`}>
              <p className="font-display text-3xl mb-2">{p.name}</p>
              <p className="mb-1">
                <span className="font-display text-5xl">${p.price_cents / 100}</span>
                <span className="font-grotesk text-xs uppercase tracking-[0.15em] text-muted-foreground"> / month</span>
              </p>
              <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-white/60 mb-8">
                {p.posts === null ? "Unlimited casting posts" : `${p.posts} casting posts monthly`}
              </p>
              {trialOffer && (
                <p className="font-grotesk text-[11px] uppercase tracking-[0.15em] text-accent border border-accent/40 bg-accent/5 px-4 py-3 mb-8" data-testid={`trial-badge-${p.lookup_key}`}>
                  {p.trial_days}-day free trial — card required, nothing charged until day {p.trial_days + 1}
                </p>
              )}
                <ul className="flex flex-col gap-2 mb-10 text-sm text-white/70">
                <li className="flex items-center gap-2"><Check size={14} className="text-accent" /> Live on the board after office review</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-accent" /> Applicant alerts by email</li>
                {p.lookup_key === "plan_unlimited" ? (
                  <li className="flex items-center gap-2"><Check size={14} className="text-accent" /> Free recasting — expired calls reopen at no charge</li>
                ) : (
                  <li className="flex items-center gap-2"><Check size={14} className="text-accent" /> Recast an expired call for $19.99</li>
                )}
                <li className="flex items-center gap-2"><Check size={14} className="text-accent" /> Cancel anytime via the office</li>
              </ul>
              {current ? (
                plan?.stripe_customer_id ? (
                  <button data-testid="manage-plan-button" onClick={manage} disabled={busy === "portal"} className="btn-outline mt-auto disabled:opacity-40">
                    <Settings size={14} /> {busy === "portal" ? "Opening…" : "Manage plan"}
                  </button>
                ) : (
                  <p className="mt-auto font-grotesk text-[10px] uppercase tracking-[0.15em] text-accent" data-testid="comp-plan-note">
                    Complimentary — managed by the office
                  </p>
                )
              ) : (
                <button
                  data-testid={`subscribe-${p.lookup_key}`}
                  onClick={() => subscribe(p.lookup_key)}
                  disabled={!agreed || busy === p.lookup_key}
                  className="btn-primary mt-auto disabled:opacity-40"
                >
                  {busy === p.lookup_key ? "Redirecting…" : plan ? "Switch to this" : trialOffer ? `Start ${p.trial_days}-day free trial` : "Choose this plan"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {!plan && (
        <p className="mt-6 font-grotesk text-[11px] uppercase tracking-[0.15em] text-white/50" data-testid="single-post-note">
          Or keep it simple — publish any single draft for $19.99, no subscription. Edit it any time while it's live; recasting an expired listing costs $19.99 (always free with Atelier)
        </p>
      )}
    </section>
  );
}
